// File storage utilities for handling attachments
export interface StoredFile {
  id: string;
  name: string;
  url: string;
  size: number;
  type: string;
  uploadedAt: string;
}

// In a real app, this would integrate with a proper file storage service
// For now, we'll simulate file storage with localStorage and blob URLs
class FileStorageService {
  private files = new Map<string, StoredFile>();

  async uploadFile(file: File): Promise<StoredFile> {
    const id = `file_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const url = URL.createObjectURL(file);
    
    const storedFile: StoredFile = {
      id,
      name: file.name,
      url,
      size: file.size,
      type: file.type,
      uploadedAt: new Date().toISOString(),
    };

    this.files.set(id, storedFile);
    
    // Store metadata in localStorage for persistence
    try {
      const existingFiles = JSON.parse(localStorage.getItem('uploadedFiles') || '[]');
      existingFiles.push({ ...storedFile, url: undefined }); // Don't store blob URLs
      localStorage.setItem('uploadedFiles', JSON.stringify(existingFiles));
    } catch (error) {
      console.warn('Failed to persist file metadata:', error);
    }

    return storedFile;
  }

  async getFile(id: string): Promise<StoredFile | null> {
    return this.files.get(id) || null;
  }

  async deleteFile(id: string): Promise<boolean> {
    const file = this.files.get(id);
    if (file) {
      URL.revokeObjectURL(file.url);
      this.files.delete(id);
      
      // Remove from localStorage
      try {
        const existingFiles = JSON.parse(localStorage.getItem('uploadedFiles') || '[]');
        const filteredFiles = existingFiles.filter((f: any) => f.id !== id);
        localStorage.setItem('uploadedFiles', JSON.stringify(filteredFiles));
      } catch (error) {
        console.warn('Failed to remove file metadata:', error);
      }
      
      return true;
    }
    return false;
  }

  async listFiles(): Promise<StoredFile[]> {
    return Array.from(this.files.values());
  }

  // Cleanup blob URLs when they're no longer needed
  cleanup() {
    this.files.forEach(file => {
      if (file.url.startsWith('blob:')) {
        URL.revokeObjectURL(file.url);
      }
    });
    this.files.clear();
  }
}

export const fileStorage = new FileStorageService();

// Helper function to validate file types
export const validateFileType = (file: File, allowedTypes: string[]): boolean => {
  return allowedTypes.includes(file.type);
};

// Helper function to validate file size (in MB)
export const validateFileSize = (file: File, maxSizeMB: number): boolean => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

// Helper function to format file size
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};